import { useGlobalStore } from "controller/store/globalStore";
import type { RequestOptions } from "mixpanel-browser";
import { useEffect } from "react";
import { mixpanel } from "utilz/mixpanel";

export type AnalyticsEventKeys =
  | "skills_visited"
  | "skill_created"
  | "skill_category_created"
  | "skills_imported"
  | "skills_import_failed"
  | "home_page_visited"
  | "company_profile_visited"
  | "company_profile_updated"
  | "resource_page_visited"
  | "resource_added"
  | "resource_updated"
  | "resource_deleted"
  | "team_member_created"
  | "team_member_resend_invite_sent"
  | "team_member_updated"
  | "team_member_permission_updated"
  | "team_member_primary_board_updated"
  | "team_member_board_assigned"
  | "team_member_ai_routing_enabled"
  | "team_member_skill_added"
  | "team_member_working_hours_updated"
  | "team_member_skill_proficiency_updated"
  | "subscription_cancelled"
  | "subscription_page_visited"
  | "subscription_payment_initiated"
  | "subscription_payment_cancelled"
  | "subscription_payment_completed"
  | "subscription_payment_seat_count_updated"
  | "security_page_visited"
  | "mfa_settings_enabled"
  | "mfa_settings_disabled"
  | "system_app_page_visited"
  | "system_app_download_clicked"
  | "mobile_app_page_visited"
  | "teams_app_page_visited"
  | "teams_app_install_clicked"
  | "support_desk_email_copy_clicked"
  | "sla_page_visited"
  | "sla_added"
  | "sla_updated"
  | "sla_policy_created"
  | "sla_policy_toggled"
  | "sla_escalation_configured"
  | "ticket_sla_status_changed"
  | "ticket_sla_breached"
  | "ticket_sla_met"
  | "quality_assurance_visited"
  | "quality_assurance_added"
  | "quality_assurance_deleted"
  | "custom_field_page_visited"
  | "custom_field_added"
  | "custom_field_updated"
  | "tags_page_visited"
  | "tags_page_added"
  | "tags_page_updated"
  | "boards_page_visited"
  | "boards_page_added"
  | "boards_page_updated"
  | "board_created"
  | "board_ai_routing_enabled"
  | "board_team_member_linked"
  | "board_primary_member_set"
  | "checklist_page_visited"
  | "checklist_added"
  | "checklist_updated"
  | "templates_page_visited"
  | "templates_added"
  | "templates_updated"
  | "canned_notes_page_visited"
  | "canned_notes_added"
  | "canned_notes_updated"
  | "archive_page_visited"
  | "archive_item_restored"
  | "tax_page_visited"
  | "tax_added"
  | "tax_updated"
  | "billing_templates_page_visited"
  | "billing_template_added"
  | "billing_template_updated"
  | "products_page_visited"
  | "products_added"
  | "products_updated"
  | "expense_page_visited"
  | "expense_added"
  | "expense_updated"
  | "billing_settings_page_visited"
  | "billing_settings_added"
  | "billing_settings_updated"
  | "integration_page_visited"
  | "integration_n_central_configuration_added"
  | "integration_n_central_configuration_disabled_enabled"
  | "integration_stripe_access_requested"
  | "integration_pax8_access_requested"
  | "how_to_clicked"
  | "homepage_team_filter_selection"
  | "homepage_buy_now_clicked"
  | "service_desk_overview_clicked"
  | "service_desk_tickets_clicked"
  | "alert_center_clicked"
  | "alert_center_redirected_to_ticket"
  | "add_ticket_clicked"
  | "ticket_created"
  | "ticket_creation_failed"
  | "tickets_list_page_all_clicked"
  | "tickets_list_page_me_clicked"
  | "tickets_list_page_status_changed"
  | "tickets_list_page_priority_changed"
  | "tickets_list_page_assign_clicked"
  | "tickets_list_page_merge_clicked"
  | "tickets_list_page_modify_clicked"
  | "tickets_list_page_archive_clicked"
  | "tickets_list_page_filters_clicked"
  | "tickets_list_page_filter_applied"
  | "tickets_list_page_boards_filter_clicked"
  | "tickets_list_page_boards_filter_applied"
  | "tickets_list_page_customer_filter_clicked"
  | "tickets_list_page_customer_filter_applied"
  | "tickets_list_page_sites_filter_clicked"
  | "tickets_list_page_sites_filter_applied"
  | "tickets_list_page_refresh_clicked"
  | "ticket_details_page_visited"
  | "tickets_detail_page_chat_mode_clicked"
  | "tickets_detail_page_chat_mode_selected"
  | "tickets_detail_page_conversation_filter_clicked"
  | "tickets_detail_page_conversation_filter_applied"
  | "tickets_detail_page_chat_action_plus_clicked"
  | "tickets_detail_page_chat_action_plus_canned_notes_clicked"
  | "tickets_detail_page_canned_notes_send"
  | "tickets_detail_page_chat_action_plus_attachments_clicked"
  | "tickets_detail_page_chat_action_plus_notes_time_clicked"
  | "tickets_detail_page_self_chat_message_created"
  | "tickets_detail_page_tags_clicked"
  | "tickets_detail_page_tags_added"
  | "tickets_detail_page_assignees_clicked"
  | "tickets_detail_page_assignees_schedule_applied"
  | "tickets_detail_page_assignees_added"
  | "tickets_detail_page_timer_started"
  | "tickets_detail_page_timer_stopped"
  | "tickets_detail_page_notes_added"
  | "tickets_detail_page_notes_time_clicked"
  | "tickets_detail_page_notes_time_applied"
  | "whats_new_clicked"
  | "project_page_visited" // newly added
  | "create_project_clicked"
  | "project_created"
  | "qa_board_visited"
  | "customer_page_visited"
  | "create_customer"
  | "user_page_visited"
  | "create_user"
  | "report_page_visited"
  | "billing_page_visited" //clarify
  | "control_center_visited"
  | "company_profile_visited"
  | "resource_page_visited"
  | "create_resource"
  | "ms_teams_page_visited"
  | "desktop_app_page_visited"
  | "desktop_app_download_clicked"
  | "mobile_app_page_visited"
  | "email_page_visited"
  | "integration_page_visited"
  | "tax_page_visited"
  | "tax_added"
  | "billing_templates_page_visited"
  | "products_page_visited"
  | "expense_page_visited"
  | "billing_settings_page_visited"
  | "custom_mail_access_requested"
  | "support_email_clicked"
  | "mailbox_added"
  | "billing_mailbox_connected"
  | "add_user_clicked"
  | "chat_message_hub_opened"
  | "trial_extend_requested"
  | "chat_message_access_specifier"
  | "add_role_clicked"
  | "role_created_successfully"
  | "create_ticket_assign_me"
  | "ticket_assign_me"
  | "ticket_unassign_me"
  | "ticket_assign_team"
  | "ticket_escalate"
  | "create_ticket_assign_team"
  | "automation_join_waitlist"
  | "automation_created"
  | "automation_updated"
  | "automation_status_updated"
  | "automation_deleted"
  | "tech_usage_count_submitted"
  | "technician_added"
  | "company_profile_submitted"
  | "app_preferences_selected"
  | "end_user_count_submitted"
  | "sample_data_imported"
  | "sample_data_skipped"
  | "email_connected"
  | "level_connection_success"
  | "connect_level_clicked"
  | "connect_ninja_one_clicked"
  | "connect_qb_clicked"
  | "connect_ncentral_clicked"
  | "ncentral_connection_success"
  | "ninja_one_connection_success"
  | "qb_connection_success"
  | "discount_page_visited"
  | "discount_added"
  | "discount_updated"
  | "connect_hudu_clicked"
  | "hudu_connection_success"
  | "vanity_domain_added"
  | "source_submitted"
  | "datto_connection_success"
  | "connect_datto_clicked"
  | "xero_connection_success"
  | "connect_xero_clicked"
  | "connect_stripe_clicked"
  | "stripe_connection_success"
  | "stripe_disconnected"
  | "stripe_payment_link_disabled"
  | "stripe_invoice_mail_sent_for_due_amount"
  | "stripe_invoice_mail_sent_for_overdue_amount"
  | "stripe_invoice_mail_sent_for_paid_amount_manually"
  | "pax8_connection_started"
  | "pax8_credentials_submitted"
  | "pax8_connection_succeeded"
  | "pax8_connection_failed"
  | "pax8_connection_disconnected"
  | "pax8_customer_mapping_viewed"
  | "pax8_customer_sync_completed"
  | "pax8_customer_sync_failed"
  | "pax8_customer_mapping_next_clicked"
  | "pax8_customer_mapping_updated"
  | "pax8_subscription_mapping_viewed"
  | "pax8_subscription_mapping_updated"
  | "pax8_subscription_invoice_mapping_updated"
  | "pax8_subscription_sync_completed"
  | "pax8_subscription_sync_failed"
  | "pax8_setup_completed"
  | "shortcut_new_ticket_opened"
  | "shortcut_new_customer_opened"
  | "shortcut_new_user_opened"
  | "invoice_list_viewed"
  | "invoice_created"
  | "invoice_viewed"
  | "invoice_edited"
  | "invoice_sent"
  | "invoice_status_changed"
  | "invoice_payment_recorded"
  | "invoice_synced_to_xero"
  | "invoice_synced_to_qb"
  | "recurring_invoice_list_viewed"
  | "recurring_invoice_created"
  | "recurring_invoice_viewed"
  | "recurring_invoice_edited"
  | "ready_to_bill_page_visited"
  | "ready_to_bill_item_viewed"
  | "ready_to_bill_converted_to_invoice"
  | "billing_template_created"
  | "billing_template_used"
  | "contract_list_viewed"
  | "contract_created"
  | "contract_viewed"
  | "kb_folder_created"
  | "kb_article_created"
  | "kb_article_imported"
  | "kb_article_page_visited"
  | "kb_folder_page_visited"
  | "kb_article_saved_as_draft"
  | "kb_article_published"
  | "kb_article_deleted"
  | "kb_article_unpublished"
  | "kb_version_deleted"
  | "book_a_demo_clicked"
  | "org_type"
  | "psa_migration_from"
  | "itsm_migration_from"
  | "imp_rmm_tools"
  | "imp_integrations"
  | "team_size_range"
  | "end_user_range"
  | "onb_improvements"
  | "attribution_source"
  | "onb_modal_completed"
  | "onb_drawer_completed"
  | "onb_sidebar_signedup_clicked"
  | "onb_sidebar_supportmail_clicked"
  | "onb_sidebar_team_clicked"
  | "onb_sidebar_branding_clicked"
  | "onb_sidebar_customers_clicked"
  | "onb_sidebar_apps_clicked"
  | "onb_sidebar_rmm_clicked"
  | "onb_bookademo_clicked"
  | "onb_chatsupport_clicked"
  | "onb_welcome_completed"
  | "onb_marked_all_complete"
  | "onb_connect_support_mail_clicked"
  | "onb_connect_support_mail_doc_clicked"
  | "onb_connect_support_mail_completed"
  | "onb_workflows_clicked"
  | "onb_add_teammates_clicked"
  | "onb_add_teammates_doc_clicked"
  | "onb_add_teammates_completed"
  | "onb_roles_and_permissions_clicked"
  | "onb_branding_clicked"
  | "onb_branding_doc_clicked"
  | "onb_branding_completed"
  | "onb_create_customer_clicked"
  | "onb_create_customer_doc_clicked"
  | "onb_create_customer_completed"
  | "onb_download_apps_clicked"
  | "onb_download_apps_doc_clicked"
  | "onb_download_apps_completed"
  | "onb_connect_rmm_clicked"
  | "onb_connect_rmm_doc_clicked"
  | "onb_connect_rmm_completed"
  | "recurring_tickets_page_visited"
  | "recurring_enabled_on_ticket_creation"
  | "recurring_ticket_created"
  | "recurring_ticket_paused"
  | "recurring_ticket_resumed"
  | "recurring_ticket_deleted"
  | "recurring_ticket_next_run_skipped"
  | "recurring_ticket_scheduled_date_skipped"
  | "recurring_ticket_bulk_paused"
  | "recurring_ticket_bulk_resumed"
  | "recurring_ticket_bulk_deleted"
  | "recurring_ticket_bulk_next_run_skipped"
  | "recurring_ticket_create_now_clicked"
  // ─── Helena V2 (naming: helena_<area>_<object>_<action>) ──────────────
  // A/B/C experiment cohort
  | "helena_variant_assigned"
  // Panel lifecycle
  | "helena_panel_opened"
  | "helena_panel_closed"
  | "helena_panel_expanded"
  | "helena_panel_restored"
  | "helena_panel_engaged"
  // Tab navigation
  | "helena_tab_switched"
  // Solution tab
  | "helena_solution_loaded"
  | "helena_solution_render_time"
  | "helena_solution_accepted"
  | "helena_attribution_clicked"
  | "helena_why_suggestion_opened"
  | "helena_helpful_thumbs_up"
  | "helena_helpful_thumbs_down"
  // Drilldown panels
  | "helena_drilldown_opened"
  | "helena_drilldown_closed"
  | "helena_similar_ticket_clicked"
  | "helena_kb_article_clicked"
  | "helena_vendor_doc_clicked"
  | "helena_why_view_all_tickets"
  // Compose tab
  | "helena_compose_mode_switched"
  | "helena_compose_quick_intent_clicked"
  | "helena_compose_quick_refine_clicked"
  | "helena_compose_regenerated"
  | "helena_compose_refreshed"
  | "helena_compose_sent_to_chat"
  // Sentiment tab
  | "helena_sentiment_viewed"
  // ─── Role profiles ───────────────────────────────────────────────────
  // `role_profile_skill_added` is for skills added to an EXISTING profile;
  // creation carries its own `skill_count` so the two never double-count.
  | "role_profile_created"
  | "role_profile_skill_added"
  | "role_profile_applied_to_team_member"
  | "role_profile_applied_to_multiple_team_members"
  | "starter_skill_profile_customized"
  | "role_profile_deleted"
  // ─── Billing roles (the Work Role attribute, renamed) ────────────────
  | "billing_role_created"
  | "billing_role_updated"
  | "billing_role_assigned_to_team_member"
  | "billing_role_deleted"
  // ─── User badge (customer-user category / crown tier) ────────────────
  // One event for the whole lifecycle; `action` is assigned | changed |
  // removed.
  | "user_badge_updated"
  // ─── Service categories ──────────────────────────────────────────────
  | "service_category_created"
  | "service_subcategory_created"
  | "service_category_type_added"
  | "subcategory_severity_updated"
  | "service_category_deleted"
  | "service_subcategory_deleted"
  // ─── Service statuses ────────────────────────────────────────────────
  | "service_status_created"
  | "service_status_updated"
  | "service_status_deleted"
  | "ai_home_screen_viewed"
  | "ai_screen_toggled"
  | "use_ai_screen_clicked"
  | "use_legacy_screen_clicked"
  | "ai_new_chat_started"
  | "ai_query_submitted"
  | "ai_response_received"
  | "ai_response_failed"
  | "ai_turn_interrupted"
  | "ai_turn_resumed"
  | "ai_result_action_taken"
  | "ai_chat_pinned"
  | "ai_chat_unpinned"
  | "ai_chat_searched"
  | "ai_chat_opened_from_history"
  | "ai_proactive_suggestion_clicked"
  | "ai_dictation_mic_clicked"
  | "ai_dictation_accepted"
  | "ai_dictation_rejected"
  | "ai_session_ended"
  | "calendar_event_clicked"
  | "home_overview_card_clicked"
  | "home_time_filter_changed"
  | "home_me_team_toggled"
  | "agent_entry_point_viewed"
  | "agent_enabled"
  | "agent_disabled"
  | "agent_config_started"
  | "agent_config_step_viewed"
  | "agent_config_step_completed"
  | "agent_config_validation_error"
  | "agent_config_abandoned"
  | "agent_config_completed"
  | "agent_config_setting_changed"
  | "agent_mode_changed"
  | "agent_suggestion_viewed"
  | "agent_suggestion_accepted"
  | "agent_suggestion_edited"
  | "agent_suggestion_rejected"
  | "agent_reasoning_viewed"
  | "take_control_clicked"
  | "agent_low_confidence_resolved"
  | "dispatch_module_viewed"
  | "dispatch_module_exited"
  | "dispatch_view_changed"
  | "dispatch_scope_changed"
  | "dispatch_date_changed"
  | "dispatch_colour_by_changed"
  | "dispatch_sla_priority_filter_selected"
  | "dispatch_queue_filter_applied"
  | "dispatch_board_filter_applied"
  | "dispatch_technician_filter_changed"
  | "dispatch_unscheduled_opened"
  | "dispatch_unscheduled_searched"
  | "dispatch_calendar_event_opened"
  | "dispatch_create_opened"
  | "dispatch_create_tab_changed"
  | "dispatch_event_created"
  | "dispatch_ticket_scheduled"
  | "dispatch_event_rescheduled"
  | "dispatch_create_abandoned"
  | "dispatch_action_failed";

export const addMixpanelEvent = ({
  event,
  data,
  options,
}: {
  event: AnalyticsEventKeys;
  // biome-ignore lint/suspicious/noExplicitAny: Mixpanel accepts flexible data types
  data?: Record<string, any>;
  options?: RequestOptions;
}) => {
  if (
    import.meta.env.VITE_APP_NODE_ENV === "UAT" ||
    import.meta.env.VITE_APP_NODE_ENV === "PROD"
  ) {
    const { mspId, companyName, resourceId, email, mspEmail } =
      useGlobalStore.getState();
    mixpanel.track(
      event,
      {
        msp_id: mspId,
        msp: companyName,
        user_id: resourceId,
        user_email: email,
        msp_email: mspEmail,
        ...data,
      },
      options,
    );
  }
};

/**
 * Sets properties on the caller's own Mixpanel people profile.
 *
 * `mixpanel.identify` runs at login (MainWrapper), so these attach to a real
 * user rather than an anonymous device. Environment-gated exactly like
 * `addMixpanelEvent` — profiles should not be written from local builds.
 *
 * `onceProperties` go through `set_once`, for facts that should keep their
 * first value: when someone first did a thing, not the last time they repeated
 * it.
 */
export const setMixpanelPeopleProperties = ({
  properties,
  onceProperties,
}: {
  // biome-ignore lint/suspicious/noExplicitAny: Mixpanel accepts flexible data types
  properties?: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: Mixpanel accepts flexible data types
  onceProperties?: Record<string, any>;
}) => {
  if (
    import.meta.env.VITE_APP_NODE_ENV === "UAT" ||
    import.meta.env.VITE_APP_NODE_ENV === "PROD"
  ) {
    if (properties) {
      mixpanel.people.set(properties);
    }
    if (onceProperties) {
      mixpanel.people.set_once(onceProperties);
    }
  }
};

export const useMixpanelPageViewTracker = ({
  event,
  data,
}: {
  event: AnalyticsEventKeys;
  // biome-ignore lint/suspicious/noExplicitAny: Mixpanel accepts flexible data types
  data?: Record<string, any>;
}) => {
  useEffect(() => {
    if (
      import.meta.env.VITE_APP_NODE_ENV === "UAT" ||
      import.meta.env.VITE_APP_NODE_ENV === "PROD"
    ) {
      const { mspId, companyName, resourceId, email, mspEmail } =
        useGlobalStore.getState();
      mixpanel.track(event, {
        msp_id: mspId,
        msp: companyName,
        user_id: resourceId,
        user_email: email,
        msp_email: mspEmail,
        ...data,
      });
    }
  }, [data, event]);
};
